import { useEffect, useReducer, useCallback } from "react";
import { FILTER_TYPE } from "utils/constants";
import debounce from "lodash.debounce";

const initialState = (filterDefinitions) => {
  const initialFilters = {};
  filterDefinitions.forEach((definition) => {
    initialFilters[definition.name] = definition.initialValue;
  });
  return {
    filters: initialFilters,
    query: "",
  };
};

const filterReducer = (state, action) => {
  switch (action.type) {
    case "UPDATE_FILTER": {
      const { name, value } = action.payload;
      return {
        ...state,
        filters: {
          ...state.filters,
          [name]: value,
        },
      };
    }
    case "REMOVE_FILTER": {
      const { name } = action.payload;
      const { [name]: _, ...newFilters } = state.filters;
      return {
        ...state,
        filters: newFilters,
      };
    }
    
    
    
    case "UPDATE_MULTIPLE_FILTERS": {
      const newFilters = action.payload;
      return {
        ...state,
        filters: {
          ...state.filters,
          ...newFilters,
        },
      };
    }
    case "SET_QUERY":
      return {
        ...state,
        query: action.payload,
      };
    default:
      return state;
  }
};


function useFilterHook(filterDefinitions, updatePagination) {
  const [state, dispatch] = useReducer(
    filterReducer,
    filterDefinitions,
    initialState
  );

  const updateFilter = (name, value) => {
    const filterDef = filterDefinitions.find((def) => def.name === name);
    const isUnselected = value === null || value === undefined || value === "";
  
    if (isUnselected) {
      debouncedUpdateFilter.cancel();
      dispatch({
        type: "REMOVE_FILTER",
        payload: { name },
      });
      return;
    }
    if (filterDef && filterDef.type === FILTER_TYPE.TEXT && filterDef.debounce) {
      debouncedUpdateFilter(name, value);
    } else {
      dispatch({
        type: "UPDATE_FILTER",
        payload: { name, value },
      });
    }
  };
  

  const updateMultipleFilters = (filterObj) => {
    dispatch({
      type: "UPDATE_MULTIPLE_FILTERS",
      payload: filterObj,
    });
  };

  const debouncedUpdateFilter = useCallback(
    debounce((name, value) => {
      dispatch({
        type: "UPDATE_FILTER",
        payload: { name, value },
      });
    }, 500),
    []
  );

  const generateQueryParams = () => {
    const queryParams = [];
    const searchTextValue = state.filters["searchText"];

    for (const filter of filterDefinitions) {
      const { name, type, formatQuery = null, dynamic } = filter;
      const value = state.filters[name];

      if (value !== null && value !== undefined && value !== -1) {
        if (type === FILTER_TYPE.MULTI_SELECT) {
          queryParams.push(
            `${name}=${value.map((v) => encodeURIComponent(v)).join(",")}`
          );
        } else if (type === FILTER_TYPE.DATE_RANGE_DROPDOWN) {
          const start =
            typeof formatQuery === "function"
              ? formatQuery(value.start)
              : value.start;
          const end =
            typeof formatQuery === "function"
              ? formatQuery(value.end)
              : value.end;
          queryParams.push(`startDate=${encodeURIComponent(start)}`);
          queryParams.push(`endDate=${encodeURIComponent(end)}`);
        } else if (dynamic) {
          if (searchTextValue && searchTextValue.length >= 3) {
            queryParams.push(`key=${name}`);
            queryParams.push(`value=${encodeURIComponent(value)}`);
          }
        } else {
          queryParams.push(`${name}=${encodeURIComponent(value)}`);
        }
      }
    }

    return queryParams.join("&");
  };

  const updateQueries = () => {
    if (typeof updatePagination === "function") updatePagination(1);
    dispatch({ type: "SET_QUERY", payload: generateQueryParams() });
  };

  useEffect(() => {
    updateQueries();
  }, [state.filters]);

  return {
    filters: state.filters,
    updateFilter,
    updateMultipleFilters,
    generateQueryParams,
    query: state.query,
  };
}

export default useFilterHook;
